/**
 * SPA State Controller
 * Handles transitions between the 4 defined sections:
 * 1. Home/Quick Actions
 * 2. Scanner View
 * 3. Product Result View
 * 4. Success/Cart View
 */

import { renderProductCards as renderProductCardsModule } from "./product-card-renderer.js";
import { StrichSDK, BarcodeReader } from "@pixelverse/strichjs-sdk";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const ScannerApp = {
  // ============================================================================
  // INITIALIZATION & SETUP
  // ============================================================================

  initialized: false, // Guard to prevent multiple initializations.

  /**
   * Initializes the ScannerApp.
   * Called on DOMContentLoaded.
   * Execution order: 1
   */
  init() {
    // Prevent multiple initializations.
    if (this.initialized) {
      return;
    }

    this.views = document.querySelectorAll(".scanner-spa__view");
    this.barcodeReader = null; // Store barcode reader instance.
    this.sdkInitialized = false; // Track SDK initialization status.
    this.currentProduct = null; // Current product being viewed.
    this.currentProducts = null; // All products from barcode search.
    this.recommendationProducts = null; // Recommended products.

    this.bindEvents();
    this.setupCartListeners();
    this.initializeStrichSDK();
    this.initialized = true; // Mark as initialized.
    console.log("ScannerApp initialized");
  },

  /**
   * Sets up event listeners for user interactions.
   * Execution order: 2 (called from init).
   */
  bindEvents() {
    const wrapper = document.getElementById("scanner-spa");

    if (wrapper) {
      // Use a single delegated event listener for all click events.
      wrapper.addEventListener("click", (event) => {
        // Look for the ID specifically or a class.
        const trigger = event.target.closest("#open-barcode-scanner");
        const closeBtn = event.target.closest("#close-scanner");
        const buyNowBtn = event.target.closest(
          ".advanced-options-card__buy-btn",
        );

        if (trigger) {
          event.preventDefault();
          event.stopPropagation(); // Prevent event from bubbling further.
          this.transitionTo("scanner");
        }

        if (closeBtn) {
          event.preventDefault();
          this.transitionTo("home");
        }

        // Listen for buy button clicks and wait for cart update.
        if (buyNowBtn) {
          const variantId = buyNowBtn.dataset.variantId;
          if (variantId) {
            // Set up a one-time listener for cart update after button click.
            const handleCartUpdate = async () => {
              // Wait a bit for the cart to update.
              setTimeout(async () => {
                try {
                  const cartRes = await fetch("/cart.js");
                  if (cartRes.ok) {
                    const cartData = await cartRes.json();
                    await this.handleCartUpdateAfterAdd(variantId, cartData);
                    this.transitionTo("success");

                    // Fetch and render recommendations.
                    if (this.currentProduct?.id) {
                      this.fetchRecommendations(this.currentProduct.id);
                    }
                  }
                } catch (error) {
                  console.error("Error fetching cart:", error);
                  await this.handleCartUpdateAfterAdd(variantId);
                  this.transitionTo("success");

                  // Fetch and render recommendations.
                  if (this.currentProduct?.id) {
                    this.fetchRecommendations(this.currentProduct.id);
                  }
                }
              }, 500); // Wait 500ms for cart update to complete.
            };

            // Listen for cart update event.
            if (typeof PUB_SUB_EVENTS !== "undefined") {
              const cartUpdateHandler = () => {
                handleCartUpdate();
                document.removeEventListener(
                  PUB_SUB_EVENTS.cartUpdate,
                  cartUpdateHandler,
                );
              };
              document.addEventListener(
                PUB_SUB_EVENTS.cartUpdate,
                cartUpdateHandler,
              );
            } else {
              // Fallback: just wait and check.
              handleCartUpdate();
            }
          }
        }
      });
    }
    // Removed duplicate direct event listener - wrapper delegation handles it.
  },

  /**
   * Sets up cart update event listeners.
   * Execution order: 3 (called from init).
   */
  setupCartListeners() {
    // Listen for cart updates from advanced-options-card or other components.
    if (
      typeof publish !== "undefined" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      document.addEventListener(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        console.log("Cart update event received:", event.detail);
        // When cart is updated, check if we should show success view.
        const currentView = document.querySelector(
          ".scanner-spa__view--active",
        );
        const isResultView = currentView && currentView.id === "view-result";
        const isSuccessView = currentView && currentView.id === "view-success";

        // Get variant ID from event detail.
        const variantId = event.detail?.variantId;

        // Always fetch fresh cart data from /cart.js.
        try {
          const cartRes = await fetch("/cart.js");
          if (cartRes.ok) {
            const cartData = await cartRes.json();
            console.log("Cart data fetched:", cartData);

            if (isResultView) {
              // Update success view with fresh cart data.
              await this.handleCartUpdateAfterAdd(variantId, cartData);

              // Transition to success view.
              this.transitionTo("success");

              // Fetch and render recommendations.
              const product = variantId
                ? this.findProductByVariantId(variantId)
                : null;
              if (product?.id) {
                this.fetchRecommendations(product.id);
              } else if (this.currentProduct?.id) {
                this.fetchRecommendations(this.currentProduct.id);
              }
            } else if (isSuccessView) {
              // Already in success view - just update it with new cart data.
              await this.updateSuccessViewFromCart(cartData);
            }
          } else {
            console.error("Failed to fetch cart data");
            // Still transition but with fallback data.
            if (isResultView) {
              if (variantId) {
                await this.handleCartUpdateAfterAdd(variantId);
              }
              this.transitionTo("success");

              // Fetch and render recommendations.
              const product = variantId
                ? this.findProductByVariantId(variantId)
                : null;
              if (product?.id) {
                this.fetchRecommendations(product.id);
              } else if (this.currentProduct?.id) {
                this.fetchRecommendations(this.currentProduct.id);
              }
            } else if (isSuccessView && variantId) {
              await this.handleCartUpdateAfterAdd(variantId);
            }
          }
        } catch (error) {
          console.error("Error fetching cart:", error);
          // Still transition but with fallback data.
          if (isResultView) {
            if (variantId) {
              await this.handleCartUpdateAfterAdd(variantId);
            }
            this.transitionTo("success");

            // Fetch and render recommendations.
            const product = variantId
              ? this.findProductByVariantId(variantId)
              : null;
            if (product?.id) {
              this.fetchRecommendations(product.id);
            } else if (this.currentProduct?.id) {
              this.fetchRecommendations(this.currentProduct.id);
            }
          } else if (isSuccessView && variantId) {
            await this.handleCartUpdateAfterAdd(variantId);
          }
        }
      });
    }

    // Also listen for custom events from advanced-options-card.
    document.addEventListener("advanced-options-card:added", async (event) => {
      const { variantId, productId } = event.detail || {};
      if (variantId) {
        // Fetch fresh cart data.
        try {
          const cartRes = await fetch("/cart.js");
          if (cartRes.ok) {
            const cartData = await cartRes.json();
            // Update success view with the added product and cart total.
            await this.updateSuccessViewFromCart(cartData);
          } else {
            // Fallback: use handleCartUpdateAfterAdd.
            await this.handleCartUpdateAfterAdd(variantId);
          }
        } catch (error) {
          console.error("Error fetching cart:", error);
          // Fallback: use handleCartUpdateAfterAdd.
          await this.handleCartUpdateAfterAdd(variantId);
        }

        // Transition to success view.
        this.transitionTo("success");

        // Fetch and render recommendations (use the product that was added).
        const product = this.findProductByVariantId(variantId);
        if (product?.id) {
          this.fetchRecommendations(product.id);
        } else if (this.currentProduct?.id) {
          this.fetchRecommendations(this.currentProduct.id);
        }
      }
    });
  },

  // ============================================================================
  // STRICH SDK & BARCODE SCANNER
  // ============================================================================

  /**
   * Initialize STRICH SDK (only once on page load).
   * Execution order: 4 (called from init).
   * @returns {Promise<void>}
   */
  async initializeStrichSDK() {
    const licenseKey = window.barcodeScanner.apiKey;

    try {
      await StrichSDK.initialize(licenseKey);
      this.sdkInitialized = true;
      console.log("StrichSDK initialized");
    } catch (err) {
      console.error("Failed to initialize StrichSDK:", err);
    }
  },

  /**
   * Initialize STRICH BarcodeReader and start scanning.
   * Called when transitioning to scanner view.
   * Execution order: Called from transitionTo when viewId === "scanner".
   * @returns {Promise<void>}
   */
  async initializeBarcodeReader() {
    // Check if SDK is initialized.
    if (!this.sdkInitialized) {
      console.warn("SDK not initialized yet, waiting...");
      // Wait a bit for SDK to initialize.
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (!this.sdkInitialized) {
        console.error("SDK initialization failed or timed out");
        return;
      }
    }

    // Destroy existing reader if it exists.
    if (this.barcodeReader) {
      try {
        await this.barcodeReader.destroy();
      } catch (err) {
        console.warn("Error destroying existing barcode reader:", err);
      }
      this.barcodeReader = null;
    }

    console.log("Initializing BarcodeReader");
    // See https://docs.strich.io/reference/interfaces/Configuration.html for all available options.
    let configuration = {
      selector: "#strich-barcode-scanner-host",
      frameSource: {
        resolution: "full-hd",
      },
      engine: {
        symbologies: [
          "ean13",
          "ean8",
          "upca",
          "upce",
          "code128",
          "code39",
          "qr",
        ],
        duplicateInterval: 1500,
      },
    };

    try {
      // Initialize BarcodeReader and start scanning barcodes.
      this.barcodeReader = await new BarcodeReader(configuration).initialize();
      this.barcodeReader.detected = async (detections) => {
        if (detections && detections.length > 0) {
          // Destroy scanner after detection.
          if (this.barcodeReader) {
            try {
              await this.barcodeReader.destroy();
            } catch (err) {
              console.warn("Error destroying barcode reader:", err);
            }
            this.barcodeReader = null;
          }
          // Fetch product by barcode.
          await this.fetchProductByBarcode(detections[0].data || detections[0]);
        }
      };
      await this.barcodeReader.start();
      console.log("BarcodeReader started successfully");
    } catch (err) {
      console.error("Failed to initialize BarcodeReader:", err);
      this.barcodeReader = null;
    }
  },

  /**
   * Stop and destroy the barcode reader.
   * Called when leaving scanner view.
   * Execution order: Called from transitionTo when leaving scanner view.
   * @returns {Promise<void>}
   */
  async stopBarcodeReader() {
    if (this.barcodeReader) {
      try {
        await this.barcodeReader.destroy();
        console.log("BarcodeReader stopped");
      } catch (err) {
        console.warn("Error stopping barcode reader:", err);
      }
      this.barcodeReader = null;
    }
  },

  // ============================================================================
  // VIEW TRANSITIONS
  // ============================================================================

  /**
   * Transitions between different views in the SPA.
   * Manages scanner lifecycle (start/stop) based on view.
   * Execution order: Called from various event handlers.
   * @param {string} viewId - The view ID to transition to ("home", "scanner", "result", "success").
   * @returns {Promise<void>}
   */
  async transitionTo(viewId) {
    this.views.forEach((view) =>
      view.classList.remove("scanner-spa__view--active"),
    );
    const target = document.getElementById(`view-${viewId}`);
    if (target) target.classList.add("scanner-spa__view--active");

    // Handle scanner lifecycle based on view.
    if (viewId === "scanner") {
      // Initialize scanner when entering scanner view.
      // Small delay to ensure DOM is ready.
      setTimeout(() => {
        this.initializeBarcodeReader();
      }, 100);
    } else {
      // Stop scanner when leaving scanner view.
      await this.stopBarcodeReader();
    }

    // Dispatch custom event for Strich.io to start/stop.
    window.dispatchEvent(
      new CustomEvent("spa:view-change", { detail: { view: viewId } }),
    );

    if (viewId === "success" || viewId === "scanner") {
      // Scroll to success header.
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "instant",
        });
      }, 750);
    }
  },

  // ============================================================================
  // PRODUCT SEARCH & DISPLAY
  // ============================================================================

  /**
   * Fetches products by barcode and displays them in the result view.
   * Called when barcode is detected or manually triggered.
   * Execution order: Called from barcode detection handler or manual trigger.
   * @param {string} barcode - The barcode to search for.
   * @returns {Promise<void>}
   */
  async fetchProductByBarcode(barcode) {
    this.transitionTo("result");
    const resultContent = document.getElementById("result-content-area");
    resultContent.innerHTML =
      '<div class="loading">Searching B2B Catalog...</div>';

    try {
      // Fetching the custom JSON view.
      const response = await fetch(
        `/search?q=${barcode}&type=product&view=json`,
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        // Clear loading state.
        resultContent.innerHTML = "";

        // Store all products for cart functionality.
        this.currentProducts = data.results;
        // Store the first product for recommendations (can be updated if needed).
        this.currentProduct = data.results[0];

        // Count total number of variant cards to determine if carousel is needed.
        let totalVariants = 0;
        data.results.forEach((product) => {
          totalVariants += product.variants?.length || 0;
        });

        // Create a single wrapper for all variant cards.
        const productGroupWrapper = document.createElement("div");
        productGroupWrapper.className = "scanner-spa__product-group";

        // If more than 2 variants, wrap in Swiper structure.
        if (totalVariants >= 2) {
          productGroupWrapper.className += " swiper";
          const swiperWrapper = document.createElement("div");
          swiperWrapper.className = "swiper-wrapper";

          // Render cards for all products with matching barcode.
          data.results.forEach((product) => {
            // Render product cards and get HTML string.
            const cardsHtml = renderProductCardsModule({
              product,
              container: null, // Get HTML string instead of rendering directly.
              onProductStored: null, // Products already stored above.
            });

            if (cardsHtml) {
              // Create a temporary container to parse HTML.
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = cardsHtml;
              // Wrap each card-wrapper in a swiper-slide.
              const cardWrappers = tempDiv.querySelectorAll(".card-wrapper");
              cardWrappers.forEach((cardWrapper) => {
                const slide = document.createElement("div");
                slide.className = "swiper-slide";
                slide.appendChild(cardWrapper.cloneNode(true));
                swiperWrapper.appendChild(slide);
              });
            }
          });

          productGroupWrapper.appendChild(swiperWrapper);
          resultContent.appendChild(productGroupWrapper);

          // Initialize carousel after DOM is updated.
          setTimeout(() => {
            this.initCarousel(".scanner-spa__product-group.swiper");
          }, 100);
        } else {
          // Render cards normally without carousel.
          data.results.forEach((product) => {
            // Render product cards and get HTML string.
            const cardsHtml = renderProductCardsModule({
              product,
              container: null, // Get HTML string instead of rendering directly.
              onProductStored: null, // Products already stored above.
            });

            if (cardsHtml) {
              // Append variant cards to the single wrapper.
              productGroupWrapper.innerHTML += cardsHtml;
            }
          });

          // Append the wrapper with all cards to the result container.
          resultContent.appendChild(productGroupWrapper);
        }
      } else {
        const errorMessage =
          window.advancedOptionsCardTranslations?.noProductFound?.replace(
            "{{ barcode }}",
            barcode,
          ) || `No product found for ${barcode}`;
        resultContent.innerHTML = `<div class="error">${errorMessage}</div>`;
      }
    } catch (error) {
      console.error("Search Error:", error);
      const errorMessage =
        window.advancedOptionsCardTranslations?.searchFailed ||
        "Search failed. Check console.";
      resultContent.innerHTML = `<div class="error">${errorMessage}</div>`;
    }
  },

  /**
   * Finds a product that contains the specified variant ID.
   * Searches through current products, recommendation products, and fallback to current product.
   * Execution order: Called when looking up products by variant ID.
   * @param {number} variantId - The variant ID to search for.
   * @returns {Object|null} The product object containing the variant, or null if not found.
   */
  findProductByVariantId(variantId) {
    // Search through all current products (from barcode search).
    if (this.currentProducts && Array.isArray(this.currentProducts)) {
      const found = this.currentProducts.find((product) =>
        product.variants?.some((v) => v.id === parseInt(variantId)),
      );
      if (found) return found;
    }

    // Search through recommendation products.
    if (
      this.recommendationProducts &&
      Array.isArray(this.recommendationProducts)
    ) {
      const found = this.recommendationProducts.find((product) =>
        product.variants?.some((v) => v.id === parseInt(variantId)),
      );
      if (found) return found;
    }

    // Fallback to single current product.
    if (this.currentProduct) {
      const hasVariant = this.currentProduct.variants?.some(
        (v) => v.id === parseInt(variantId),
      );
      return hasVariant ? this.currentProduct : null;
    }
    return null;
  },

  // ============================================================================
  // CART OPERATIONS
  // ============================================================================

  /**
   * Adds a product variant to the cart.
   * Execution order: Called from buy button click handlers.
   * @param {number} variantId - The variant ID to add to cart.
   * @returns {Promise<void>}
   */
  async addToCart(variantId) {
    const qtyInput = document.getElementById(`qty-${variantId}`);
    const qty = qtyInput ? parseInt(qtyInput.value) : 1;

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: qty }],
        }),
      });

      if (res.ok) {
        const addResult = await res.json();

        // Find the product containing this variant.
        const product = this.findProductByVariantId(variantId);
        if (!product) {
          console.error("Product not found for variant:", variantId);
          return;
        }

        // Find the variant data from the product.
        const variant = product.variants?.find(
          (v) => v.id === parseInt(variantId),
        );
        const productTitle = product.title || "Product";
        const variantTitle =
          variant?.title && variant.title !== "Default Title"
            ? ` - ${variant.title}`
            : "";
        const productPrice = variant?.price || "N/A";

        // Update current product for recommendations (use the product that was added).
        this.currentProduct = product;

        // Fetch updated cart data.
        const cartRes = await fetch("/cart.js");
        if (cartRes.ok) {
          const cartData = await cartRes.json();
          const cartTotal = cartData.total_price || 0;

          // Update success view with product info and cart total.
          await this.updateSuccessView(
            productTitle + variantTitle,
            productPrice,
            cartTotal,
          );
        } else {
          // Fallback if cart fetch fails.
          await this.updateSuccessView(
            productTitle + variantTitle,
            productPrice,
            0,
          );
        }

        // Transition to Screen 4 (Success).
        this.transitionTo("success");
        // Trigger recommendations fetch (optional).
        this.fetchRecommendations(variantId);
      } else {
        console.error("Failed to add to cart:", await res.text());
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  },

  /**
   * Handles cart update after adding a product.
   * Finds product info and updates the success view.
   * Execution order: Called from cart update event handlers.
   * @param {number} variantId - The variant ID that was added.
   * @param {Object} [cartData] - Optional cart data (will fetch if not provided).
   * @returns {Promise<void>}
   */
  async handleCartUpdateAfterAdd(variantId, cartData = null) {
    // Find the product containing this variant.
    console.log("Finding product by variant ID:", variantId);
    const product = this.findProductByVariantId(variantId);
    console.log("Product found:", product);
    if (!product) {
      console.error("Product not found for variant:", variantId);
      return;
    }

    // Find the variant data from the product.
    const variant = product.variants?.find((v) => v.id === parseInt(variantId));
    const productTitle = product.title || "Product";
    const variantTitle =
      variant?.title && variant.title !== "Default Title"
        ? ` - ${variant.title}`
        : "";
    const productPrice = variant?.price || "N/A";

    // Use provided cart data or fetch fresh.
    if (!cartData) {
      try {
        const cartRes = await fetch("/cart.js");
        if (cartRes.ok) {
          cartData = await cartRes.json();
        }
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
    }

    console.log("Cart data:", cartData);

    const cartTotal = cartData?.total_price || 0;
    await this.updateSuccessView(
      productTitle + variantTitle,
      productPrice,
      cartTotal,
    );
  },

  /**
   * Updates the success view with fresh cart data.
   * Always fetches fresh cart data to ensure accuracy, even if cartData is provided.
   * Execution order: Called from cart update handlers.
   * @param {Object} [cartData] - Optional cart data (will be ignored, fresh data is always fetched).
   * @returns {Promise<void>}
   */
  async updateSuccessViewFromCart(cartData) {
    try {
      // Always fetch fresh cart data to ensure accuracy.
      const cartRes = await fetch("/cart.js");
      if (!cartRes.ok) {
        console.error("Failed to fetch cart data");
        return;
      }

      const data = await cartRes.json();
      const cartTotal = data.total_price || 0;
      const lastItem =
        data.items && data.items.length > 0
          ? data.items[data.items.length - 1]
          : null;

      if (lastItem) {
        const product = this.findProductByVariantId(lastItem.variant_id);
        if (product) {
          const variant = product.variants?.find(
            (v) => v.id === lastItem.variant_id,
          );
          const productTitle = product.title || "Product";
          const variantTitle =
            variant?.title && variant.title !== "Default Title"
              ? ` - ${variant.title}`
              : "";
          const productPrice =
            variant?.price || lastItem.price_formatted || "N/A";
          await this.updateSuccessView(
            productTitle + variantTitle,
            productPrice,
            cartTotal,
          );
        } else {
          await this.updateSuccessView("Product", "N/A", cartTotal);
        }
      } else {
        await this.updateSuccessView("Product", "N/A", cartTotal);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      // Fallback: use provided cartData if available.
      if (cartData) {
        const cartTotal = cartData.total_price || 0;
        const lastItem =
          cartData.items && cartData.items.length > 0
            ? cartData.items[cartData.items.length - 1]
            : null;

        if (lastItem) {
          const product = this.findProductByVariantId(lastItem.variant_id);
          if (product) {
            const variant = product.variants?.find(
              (v) => v.id === lastItem.variant_id,
            );
            const productTitle = product.title || "Product";
            const variantTitle =
              variant?.title && variant.title !== "Default Title"
                ? ` - ${variant.title}`
                : "";
            const productPrice =
              variant?.price || lastItem.price_formatted || "N/A";
            await this.updateSuccessView(
              productTitle + variantTitle,
              productPrice,
              cartTotal,
            );
          }
        }
      }
    }
  },

  /**
   * Updates the success view with product information and cart total.
   * Fetches fresh cart data to get the current total_price.
   * Execution order: Called from cart update handlers.
   * @param {string} productTitle - The product title to display.
   * @param {string} productPrice - The product price to display.
   * @param {number} [cartTotal] - Optional cart total (will be overridden by fetched data).
   * @returns {Promise<void>}
   */
  async updateSuccessView(productTitle, productPrice, cartTotal) {
    try {
      // Fetch fresh cart data to get accurate total_price.
      const cartRes = await fetch("/cart.js");
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        cartTotal = cartData.total_price || 0;
      } else {
        console.warn("Failed to fetch cart data, using provided cartTotal");
        // Use provided cartTotal as fallback if fetch fails.
        cartTotal = cartTotal || 0;
      }
    } catch (error) {
      console.error("Error fetching cart data:", error);
      // Use provided cartTotal as fallback if fetch fails.
      cartTotal = cartTotal || 0;
    }

    const successHeader = document.querySelector(
      ".scanner-spa__success-header",
    );
    if (successHeader) {
      // Format cart total as money (cartTotal is in cents from cart.js).
      const formattedTotal = this.formatMoney(cartTotal);

      // Update the product info (first p tag after h2).
      const productInfo = successHeader.querySelector("#added-product-info");
      const totalInfo = successHeader.querySelector("#current-total");

      if (productInfo) {
        productInfo.textContent = `${productTitle} - ${productPrice}`;
      }

      if (totalInfo) {
        //if cart total == 0 then show productPrice
        if (cartTotal === 0) {
          totalInfo.textContent = productPrice;
        } else {
          totalInfo.textContent = formattedTotal;
        }
      }

      // First fetch can be one step behind after add-to-cart; do a second fetch after a short delay to get the latest total.
      await new Promise((resolve) => setTimeout(resolve, 400));
      const cartRes2 = await fetch("/cart.js");
      if (cartRes2.ok && totalInfo) {
        const cartData2 = await cartRes2.json();
        const latestTotal = cartData2.total_price ?? cartTotal;
        totalInfo.textContent = this.formatMoney(latestTotal);
      }
    }
  },

  // ============================================================================
  // RECOMMENDATIONS
  // ============================================================================

  /**
   * Fetches and displays product recommendations.
   * Tries section endpoint first, then falls back to JSON endpoint.
   * Execution order: Called after adding product to cart or viewing success screen.
   * @param {number} productId - The product ID to get recommendations for.
   * @returns {Promise<void>}
   */
  async fetchRecommendations(productId) {
    if (!productId) {
      console.warn("No product ID provided for recommendations");
      return;
    }

    const recommendationsContainer = document.getElementById(
      "fbt-recommendations",
    );
    if (!recommendationsContainer) {
      console.warn("Recommendations container not found");
      return;
    }

    // Show loading state.
    recommendationsContainer.innerHTML =
      '<div class="loading">Loading recommendations...</div>';

    try {
      // Fetch recommendations from Search & Discovery API.
      // Try section endpoint first, then fallback to JSON endpoint.
      const sectionUrl = `/recommendations/products?product_id=${productId}&limit=4&section_id=b2b-recommendations`;
      const jsonUrl = `/recommendations/products.json?product_id=${productId}&limit=4`;

      let recommendations = [];

      // Try section endpoint first.
      try {
        const sectionRes = await fetch(sectionUrl);
        if (sectionRes.ok) {
          const sectionHtml = await sectionRes.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, "text/html");
          const products = doc.querySelectorAll(
            ".product-card, [data-product-id]",
          );

          // Extract product IDs from the section HTML.
          const productIds = Array.from(products)
            .map((el) => {
              const id =
                el.getAttribute("data-product-id") ||
                el
                  .querySelector("[data-product-id]")
                  ?.getAttribute("data-product-id");
              return id;
            })
            .filter(Boolean);

          // Fetch full product data for each recommendation.
          if (productIds.length > 0) {
            const productPromises = productIds.map((id) =>
              fetch(`/search?q=id:${id}&type=product&view=json`)
                .then((res) => res.json())
                .then((data) => data.results?.[0])
                .catch(() => null),
            );
            recommendations = (await Promise.all(productPromises)).filter(
              Boolean,
            );
          }
        }
      } catch (error) {
        console.log("Section endpoint failed, trying JSON endpoint:", error);
      }

      // Fallback to JSON endpoint if section didn't work.
      if (recommendations.length === 0) {
        const jsonRes = await fetch(jsonUrl);
        if (jsonRes.ok) {
          const jsonData = await jsonRes.json();
          if (jsonData.products && jsonData.products.length > 0) {
            // Transform recommendations to match search.json format.
            const productPromises = jsonData.products.map((product) => {
              // Fetch full product data with variants.
              return fetch(`/search?q=id:${product.id}&type=product&view=json`)
                .then((res) => res.json())
                .then((data) => data.results?.[0])
                .catch(() => null);
            });
            recommendations = (await Promise.all(productPromises)).filter(
              Boolean,
            );
          }
        }
      }

      // Render recommendations using renderProductCards module.
      if (recommendations.length > 0) {
        recommendationsContainer.innerHTML = "";

        // Store recommendation products for cart functionality.
        this.recommendationProducts = recommendations;

        // If more than 2 recommendations, wrap in Swiper structure.
        if (recommendations.length >= 2) {
          const swiperContainer = document.createElement("div");
          swiperContainer.className =
            "swiper scanner-spa__recommendations-swiper";
          const swiperWrapper = document.createElement("div");
          swiperWrapper.className = "swiper-wrapper";

          // Render each recommendation product.
          recommendations.forEach((product) => {
            if (product) {
              // Prepare recommendation product with only first variant.
              const recommendationProduct = {
                ...product,
                variants: product.variants?.slice(0, 1) || [],
              };

              // Render the product card directly to the wrapper.
              const cardsHtml = renderProductCardsModule({
                product: recommendationProduct,
                container: null, // Don't render to container, get HTML string instead.
              });

              if (cardsHtml) {
                // Create a temporary container to parse HTML.
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = cardsHtml;
                // Wrap each card-wrapper in a swiper-slide.
                const cardWrappers = tempDiv.querySelectorAll(".card-wrapper");
                cardWrappers.forEach((cardWrapper) => {
                  const slide = document.createElement("div");
                  slide.className = "swiper-slide";
                  slide.appendChild(cardWrapper.cloneNode(true));
                  swiperWrapper.appendChild(slide);
                });
              }
            }
          });

          swiperContainer.appendChild(swiperWrapper);
          recommendationsContainer.appendChild(swiperContainer);

          // Initialize carousel after DOM is updated.
          setTimeout(() => {
            this.initCarousel(".scanner-spa__recommendations-swiper");
          }, 100);
        } else {
          // Render recommendations normally without carousel.
          recommendations.forEach((product) => {
            if (product) {
              // Create a wrapper for each recommendation.
              const productWrapper = document.createElement("div");
              productWrapper.className = "scanner-spa__recommendation-item";

              // Prepare recommendation product with only first variant.
              const recommendationProduct = {
                ...product,
                variants: product.variants?.slice(0, 1) || [],
              };

              // Render the product card directly to the wrapper.
              const cardsHtml = renderProductCardsModule({
                product: recommendationProduct,
                container: null, // Don't render to container, get HTML string instead.
              });

              if (cardsHtml) {
                productWrapper.innerHTML = cardsHtml;
                recommendationsContainer.appendChild(productWrapper);
              }
            }
          });
        }
      } else {
        recommendationsContainer.innerHTML =
          '<p class="no-recommendations">No recommendations available at this time.</p>';
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      recommendationsContainer.innerHTML =
        '<p class="recommendations-error">Unable to load recommendations.</p>';
    }
  },

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Formats money from cents to formatted currency string.
   * Uses Shopify's formatMoney if available, otherwise uses Intl.NumberFormat.
   * Execution order: Called from updateSuccessView.
   * @param {number} cents - The amount in cents.
   * @returns {string} The formatted currency string.
   */
  formatMoney(cents) {
    // Format money from cents (cart.js returns prices in cents).
    // Try to use Shopify's formatMoney if available.
    if (typeof window.Shopify?.formatMoney === "function") {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback formatting using Intl.NumberFormat.
    const amount = cents / 100;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  },

  // ============================================================================
  // CAROUSEL FUNCTIONALITY
  // ============================================================================

  /**
   * Initializes a Swiper carousel for the specified selector.
   * Only initializes if the element exists and has more than 2 slides.
   * @param {string} selector - The CSS selector for the Swiper container.
   * @returns {Swiper|null} The Swiper instance or null if initialization failed.
   */
  initCarousel(selector) {
    const swiperElement = document.querySelector(selector);
    if (!swiperElement) {
      console.warn(`Swiper element not found: ${selector}`);
      return null;
    }

    // Check if Swiper is already initialized on this element.
    if (swiperElement.swiper) {
      // Destroy existing instance before creating a new one.
      swiperElement.swiper.destroy(true, true);
    }

    // Count slides to ensure we have more than 2.
    const slides = swiperElement.querySelectorAll(".swiper-slide");
    if (slides.length <= 1) {
      console.log(
        `Not enough slides (${slides.length}) to initialize carousel`,
      );
      return null;
    }

    // Inject custom styles for Swiper slides.
    const styleId = "swiper-custom-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        .swiper {
          width: 100% !important;
        }

        .swiper-slide {
          height: auto !important;
        }

        .swiper-slide .product-card-wrapper {
          height: 100%;
        }
      `;
      document.head.appendChild(style);
    }

    try {
      // Initialize Swiper with navigation.
      const swiperInstance = new Swiper(swiperElement, {
        modules: [Navigation],
        slidesPerView: 1.2,
        spaceBetween: 16,
        loop: true,
        breakpoints: {
          768: {
            slidesPerView: 2.25,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 16,
          },
        },
      });

      return swiperInstance;
    } catch (error) {
      console.error("Error initializing Swiper:", error);
      return null;
    }
  },
};

// Expose ScannerApp globally for inline onclick handlers.
window.ScannerApp = ScannerApp;

// Initialize the app when DOM is ready.
document.addEventListener("DOMContentLoaded", () => ScannerApp.init());
